import { formatTimeSavedHours, sortSuccessCriteriaObjectives } from '../utils';

export function transformCommentBanksToCSV(commentBankStats) {
  if (!commentBankStats || commentBankStats.length === 0) {
    return [['Comment Bank', 'Suggestion', '% Frequency'], ['No data available', '', '']];
  }

  const headers = ['Comment Bank', 'Suggestion', '% Frequency'];
  const firstItem = commentBankStats[0];

  // If data is already in flattened format (from modal export)
  if (
    firstItem &&
    firstItem['Feedback Area'] !== undefined &&
    (firstItem['Feedback Area %'] !== undefined ||
      firstItem.Suggestion !== undefined ||
      firstItem['Suggestion %'] !== undefined)
  ) {
    const detailedHeaders = ['Feedback Area', 'Feedback Area %', 'Suggestion', 'Suggestion %'];
    const rows = commentBankStats.map((item) => [
      item['Feedback Area'] || '',
      item['Feedback Area %'] || '',
      item.Suggestion || '',
      item['Suggestion %'] || '',
    ]);
    return [detailedHeaders, ...rows];
  }

  if (firstItem && firstItem['Feedback Area'] !== undefined) {
    const rows = commentBankStats.map((item) => [
      item['Feedback Area'] || '',
      item['% Frequency'] || '0.0%',
      item['Comment Count'] ?? 0,
    ]);
    return [headers, ...rows];
  }

  // Expand suggestions AND comments (full 3-level hierarchy)
  const fullHeaders = ['Comment Bank', 'Suggestion', 'Suggestion %', 'Comment', 'Comment %'];
  const rows = [];

  commentBankStats.forEach((bank) => {
    const bankTitle = bank.title || bank.name || 'Untitled Bank';
    const suggestions = bank.suggestions || [];

    if (suggestions.length === 0) {
      // If no suggestions, show the bank itself
      const isDisplayFormat = bank.name !== undefined;
      rows.push([
        bankTitle,
        'N/A',
        '',
        '',
        isDisplayFormat
          ? `${(bank.percentage || 0).toFixed(1)}%`
          : `${(bank.usagePercentage || 0).toFixed(1)}%`,
      ]);
    } else {
      // Show each suggestion with all its comments
      suggestions.forEach((suggestion) => {
        const suggestionTitle = suggestion.title || suggestion.name || 'Untitled';
        const suggestionPct = `${(suggestion.usagePercentage || 0).toFixed(1)}%`;
        const comments = suggestion.comments || [];

        if (comments.length === 0) {
          // Suggestion with no comments
          rows.push([
            bankTitle,
            suggestionTitle,
            suggestionPct,
            'No comments',
            '',
          ]);
        } else {
          // Show each comment as a separate row
          comments.forEach((comment) => {
            rows.push([
              bankTitle,
              suggestionTitle,
              suggestionPct,
              comment.comment || comment.text || 'Untitled comment',
              `${(comment.usagePercentage || 0).toFixed(1)}%`,
            ]);
          });
        }
      });
    }
  });

  return [fullHeaders, ...rows];
}

export function transformSuccessCriteriaToCSV(successCriteriaStats) {
  if (!successCriteriaStats || successCriteriaStats.length === 0) {
    return [
      [
        'Criteria',
        'Objective',
        'Satisfied',
        'Partially Satisfied',
        'Not Satisfied',
      ],
      ['No data available', '', '', '', ''],
    ];
  }

  const headers = [
    'Criteria',
    'Objective',
    'Satisfied',
    'Partially Satisfied',
    'Not Satisfied',
  ];

  const firstItem = successCriteriaStats[0];

  if (firstItem && firstItem.Criteria !== undefined && firstItem.Objective !== undefined) {
    const rows = successCriteriaStats.map((item) => [
      item.Criteria || 'Untitled',
      item.Objective || 'Untitled',
      item.Satisfied || '0.0%',
      item.PartiallySatisfied || item['Partially Satisfied'] || '0.0%',
      item.NotSatisfied || item['Not Satisfied'] || '0.0%',
    ]);
    return [headers, ...rows];
  }

  const rows = [];

  successCriteriaStats.forEach((criteria) => {
    sortSuccessCriteriaObjectives(criteria.objectives || []).forEach((objective) => {
      rows.push([
        criteria.title || 'Untitled',
        objective.title || 'Untitled',
        `${(objective.satisfiedPercentage || 0).toFixed(1)}%`,
        `${(objective.partiallySatisfiedPercentage || 0).toFixed(1)}%`,
        `${(objective.notSatisfiedPercentage || 0).toFixed(1)}%`,
      ]);
    });
  });

  return [headers, ...rows];
}

export function transformSummaryToCSV(submissionsStats) {
  if (!submissionsStats) {
    return [['Metric', 'Value'], ['No data available', '']];
  }

  const headers = ['Metric', 'Value'];

  const rows = [
    ['Total Submissions', submissionsStats.total || 0],
    ['Classwork', submissionsStats.classwork || 0],
    ['Independent Study', submissionsStats.independent || 0],
    [
      'Task Completion Rate',
      `${(submissionsStats.taskCompletionPercentage || 0).toFixed(1)}%`,
    ],
    ['On Time Rate', `${(submissionsStats.onTimePercent || 0).toFixed(1)}%`],
    ['Time Saved', formatTimeSavedHours(submissionsStats.timeSavedHours)],
  ];

  return [headers, ...rows];
}

const RUBRICS_FIXED_COLUMNS = ['Rank', 'Student', 'Question'];

function getQuestionSerialLabel(questionNumber) {
  const serial = Number(questionNumber);
  if (!Number.isFinite(serial) || serial <= 0) return 'Q-';
  return `Q${serial}`;
}

function rubricRowObjectsToTable(rows) {
  if (!Array.isArray(rows) || rows.length === 0) {
    return [
      [...RUBRICS_FIXED_COLUMNS, 'Criterion 1'],
      ['No data available', '', '']
    ];
  }

  const fixedHeaderSet = new Set(RUBRICS_FIXED_COLUMNS);
  const allCriteriaColumns = [];

  // Collect all unique criteria column names
  rows.forEach((row) => {
    Object.keys(row || {}).forEach((key) => {
      if (!fixedHeaderSet.has(key) && !allCriteriaColumns.includes(key)) {
        allCriteriaColumns.push(key);
      }
    });
  });

  allCriteriaColumns.sort((a, b) => a.localeCompare(b));

  // Export ALL criteria columns - no chunking, exactly like UI table
  const headers = [...RUBRICS_FIXED_COLUMNS, ...allCriteriaColumns];
  const body = rows.map((row) => headers.map((header) => {
    const value = row?.[header];
    return value !== null && value !== undefined ? value : '';
  }));

  return [headers, ...body];
}

function buildRubricsDetailRows(rubricsStats, selectedRubricId = null) {
  if (!Array.isArray(rubricsStats) || rubricsStats.length === 0) {
    return [];
  }

  const taskMap = new Map();
  const studentMap = new Map();
  const questionNumberSet = new Set();
  const allGradeLevels = new Set(); // Track all unique grade levels

  rubricsStats.forEach((task) => {
    const taskId = task.taskId;
    const taskTitle = task.taskTitle || task.title || `Task ${taskId}`;

    (task.stats || []).forEach((stat) => {
      if (selectedRubricId && String(stat.rubricId || '') !== String(selectedRubricId)) {
        return;
      }

      const questionKey = stat.questionSerialNumber?.value || 'unknown';
      const questionNumber = Number(questionKey) || 0;
      if (questionNumber > 0) questionNumberSet.add(questionNumber);

      const scores = (stat.criteriaWithLevels || []).map((criteria, index) => {
        const gradeLevel = criteria.selectedLevel?.value || '';
        if (gradeLevel) allGradeLevels.add(gradeLevel); // Track grade level
        return {
          value: gradeLevel,
          criteriaTitle: criteria.criteriaTitle || `Criterion ${index + 1}`,
        };
      });

      if (!taskMap.has(taskId)) {
        taskMap.set(taskId, { taskId, taskTitle, criterias: [] });
      }
      const taskEntry = taskMap.get(taskId);
      const nextTitles = scores.map((score, index) => score.criteriaTitle || `Criterion ${index + 1}`);
      if (nextTitles.length > taskEntry.criterias.length) {
        taskEntry.criterias = nextTitles;
      }

      const studentName = stat.studentName || 'Unknown';
      if (!studentMap.has(studentName)) {
        studentMap.set(studentName, {
          name: studentName,
          scoresByTaskQuestion: new Map(),
        });
      }
      studentMap.get(studentName).scoresByTaskQuestion.set(`${taskId}-${questionNumber}`, scores);
    });
  });

  const taskGroups = Array.from(taskMap.values());
  const questionNumbers = Array.from(questionNumberSet).sort((a, b) => a - b);
  const resolvedQuestions = questionNumbers.length > 0 ? questionNumbers : [null];

  // Build rows with ALL criteria as separate columns (no chunking)
  const rows = [];
  const studentList = Array.from(studentMap.values());

  studentList.forEach((student, studentIndex) => {
    resolvedQuestions.forEach((questionNumber) => {
      const row = {
        Rank: studentIndex + 1,
        Student: student.name,
        Question: getQuestionSerialLabel(questionNumber),
      };

      // Combine all tasks' criteria into one row
      taskGroups.forEach((task) => {
        const scores = student.scoresByTaskQuestion.get(`${task.taskId}-${questionNumber}`) || [];

        // Add each criterion with combined task name and criteria name as key (matching UI)
        scores.forEach((score, index) => {
          const criteriaTitle = score.criteriaTitle || task.criterias[index] || `Criterion ${index + 1}`;
          const columnName = `${task.taskTitle} - ${criteriaTitle}`;
          row[columnName] = score.value || '';
        });
      });

      // Only add row if it has at least one score
      if (Object.keys(row).length > 3) {
        rows.push(row);
      }
    });
  });

  return rows;
}

export function transformRubricsToCSV(rubricsStats) {
  if (Array.isArray(rubricsStats) && rubricsStats.length > 0 && rubricsStats[0]?.Rank !== undefined && rubricsStats[0]?.Student !== undefined) {
    const result = rubricRowObjectsToTable(rubricsStats);
    return result;
  }

  const detailRows = buildRubricsDetailRows(rubricsStats);
  if (detailRows.length > 0) {
    const result = rubricRowObjectsToTable(detailRows);
    return result;
  }

  const headers = [
    'Student Name',
    'Student Rank',
    'Task Name',
    'Grade Level Achieved',
  ];

  return [headers, ['No data available', '', '', '']];
}

// PDF-specific transformer - returns array of chunks, each with headers and rows
export function transformRubricsToPDF(rubricsStats, maxCriteriaPerRow = 5) {
  let rows;

  if (Array.isArray(rubricsStats) && rubricsStats.length > 0 && rubricsStats[0]?.Rank !== undefined && rubricsStats[0]?.Student !== undefined) {
    rows = rubricsStats;
  } else {
    rows = buildRubricsDetailRows(rubricsStats);
  }

  if (!Array.isArray(rows) || rows.length === 0) {
    return [[
      [...RUBRICS_FIXED_COLUMNS],
      ['No data available', '', '']
    ]];
  }

  const fixedHeaderSet = new Set(RUBRICS_FIXED_COLUMNS);
  const allCriteriaColumns = [];

  // Collect all unique criteria column names
  rows.forEach((row) => {
    Object.keys(row || {}).forEach((key) => {
      if (!fixedHeaderSet.has(key) && !allCriteriaColumns.includes(key)) {
        allCriteriaColumns.push(key);
      }
    });
  });

  allCriteriaColumns.sort((a, b) => a.localeCompare(b));

  // If we have maxCriteriaPerRow or fewer, return single chunk with actual names
  if (allCriteriaColumns.length <= maxCriteriaPerRow) {
    const headers = [...RUBRICS_FIXED_COLUMNS, ...allCriteriaColumns];
    const body = rows.map((row) => headers.map((header) => {
      const value = row?.[header];
      return value !== null && value !== undefined ? value : '';
    }));
    return [[headers, ...body]];
  }

  // For more criteria, return multiple chunks, each with correct headers
  const numChunks = Math.ceil(allCriteriaColumns.length / maxCriteriaPerRow);
  const chunks = [];

  for (let chunkIndex = 0; chunkIndex < numChunks; chunkIndex++) {
    const startIdx = chunkIndex * maxCriteriaPerRow;
    const endIdx = Math.min(startIdx + maxCriteriaPerRow, allCriteriaColumns.length);
    const criteriaChunk = allCriteriaColumns.slice(startIdx, endIdx);

    // Headers for this chunk with actual criterion names
    const headers = [...RUBRICS_FIXED_COLUMNS, ...criteriaChunk];

    // Build rows for this chunk
    const chunkRows = rows.map((row) => {
      const gradesRow = [
        row.Rank || '',
        row.Student || '',
        row.Question || '',
      ];

      criteriaChunk.forEach((criteriaName) => {
        gradesRow.push(row[criteriaName] || '');
      });

      return gradesRow;
    });

    chunks.push([headers, ...chunkRows]);
  }

  return chunks;
}

export function transformShortAnswerToCSV(shortAnswerStats) {
  if (Array.isArray(shortAnswerStats) && shortAnswerStats.length > 0 && shortAnswerStats[0]?.Rank !== undefined && shortAnswerStats[0]?.Student !== undefined) {
    const firstRow = shortAnswerStats[0];
    const headers = Object.keys(firstRow);
    const rows = shortAnswerStats.map(row => headers.map(header => {
      const value = row[header];
      return value !== null && value !== undefined ? value : '';
    }));
    return [headers, ...rows];
  }

  const headers = [
    'Student Name',
    'Student Rank',
    'Task Name',
    'Question',
    'Score Numerator',
    'Score Denominator',
    'Student Average %',
  ];

  if (!shortAnswerStats) {
    return [headers, ['No data available', '', '', '', '', '', '']];
  }

  const tasks = Array.isArray(shortAnswerStats?.tasks) ? shortAnswerStats.tasks : [];

  if (tasks.length === 0) {
    return [headers, ['No data available', '', '', '', '', '', '']];
  }

  const studentAvgMap = new Map();
  tasks.forEach((task) => {
    (task?.studentStats || []).forEach((studentStat) => {
      const key = String(studentStat?.studentId ?? studentStat?.studentName ?? '');
      if (!key) return;
      if (!studentAvgMap.has(key)) {
        studentAvgMap.set(key, {
          studentName: studentStat?.studentName || 'Unknown',
          average: Number(studentStat?.average || 0),
        });
      }
    });
  });

  const rankedStudents = Array.from(studentAvgMap.entries())
    .map(([studentKey, data]) => ({ studentKey, ...data }))
    .sort((a, b) => b.average - a.average || a.studentName.localeCompare(b.studentName));
  const rankMap = new Map(rankedStudents.map((student, index) => [student.studentKey, index + 1]));

  const rows = [];
  tasks.forEach((task) => {
    const taskName = task?.taskTitle || 'Untitled Task';
    (task?.studentStats || []).forEach((studentStat) => {
      const studentKey = String(studentStat?.studentId ?? studentStat?.studentName ?? '');
      const studentName = studentStat?.studentName || 'Unknown';
      const studentRank = rankMap.get(studentKey) || '';
      const studentAverage = `${Number(studentStat?.average || 0).toFixed(1)}%`;
      const entries = Array.isArray(studentStat?.entries) ? studentStat.entries : [];

      if (entries.length === 0) {
        rows.push([studentName, studentRank, taskName, '-', '', '', studentAverage]);
        return;
      }

      entries.forEach((entry) => {
        const questionNumber = entry?.questionSerialNumber?.value;
        const questionLabel = questionNumber != null ? `Question ${questionNumber}` : 'Question';
        const awardedMarks = entry?.awardMarks ?? entry?.awardedMarks ?? '';
        const highestMarks = entry?.highestMarks ?? '';

        rows.push([
          studentName,
          studentRank,
          taskName,
          questionLabel,
          awardedMarks,
          highestMarks,
          studentAverage,
        ]);
      });
    });
  });

  return rows.length > 0 ? [headers, ...rows] : [headers, ['No data available', '', '', '', '', '', '']];
}

export function transformCommentBanksForChart(commentBankStats) {
  if (!commentBankStats || commentBankStats.length === 0) {
    return null;
  }

  return {
    labels: commentBankStats.map((bank) => bank.title || 'Untitled'),
    values: commentBankStats.map((bank) => bank.usagePercentage || 0),
  };
}

export function transformSuccessCriteriaForChart(successCriteriaStats) {
  if (!successCriteriaStats || successCriteriaStats.length === 0) {
    return null;
  }

  const labels = [];
  const satisfied = [];
  const partiallySatisfied = [];
  const notSatisfied = [];

  successCriteriaStats.forEach((criteria) => {
    sortSuccessCriteriaObjectives(criteria.objectives || []).forEach((objective) => {
      labels.push(`${criteria.title} - ${objective.title}`);
      satisfied.push(objective.satisfiedPercentage || 0);
      partiallySatisfied.push(objective.partiallySatisfiedPercentage || 0);
      notSatisfied.push(objective.notSatisfiedPercentage || 0);
    });
  });

  return {
    labels,
    datasets: {
      satisfied,
      partiallySatisfied,
      notSatisfied,
    },
  };
}

export function transformSummaryForChart(submissionsStats) {
  if (!submissionsStats) {
    return null;
  }

  return {
    labels: ['Classwork', 'Independent Study'],
    values: [
      submissionsStats.classwork || 0,
      submissionsStats.independent || 0,
    ],
  };
}
